/**
 * Payroll Service
 * Handles all payroll-related API calls
 * @module services/payroll/payrollService
 */

import httpClient from '@/utils/httpClient';
import { isValidId, createInvalidIdResponse } from '@/services/shared/validationHelpers';
import { ENDPOINTS, getEndpointPath } from '@/api/endpoints';
import { idempotencyHeaders } from '@/utils/idempotency';

const payrollService = {
  getAll: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.BASE)}?${queryString}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching payroll:', error);
      throw error;
    }
  },

  getSummaryStats: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.STATS_SUMMARY)}?${queryString}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching payroll summary stats:', error);
      throw error;
    }
  },

  getPayrollsByMonthOrYear: async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.BASE)}?${queryString}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching payrolls by month or year:', error);
      throw error;
    }
  },

  getByEmployeeId: async (employeeId, month) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.PAYROLL.BY_EMPLOYEE, employeeId), {
        params: month ? { month } : {}
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  generate: async (data) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.PAYROLL.GENERATE), data);
      return response.data;
    } catch (error) {
      console.error('Error generating payroll:', error);
      throw error;
    }
  },

  payEmployee: async (employeeId, data) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.PAYROLL.PAY_EMPLOYEE, employeeId), data, {
        headers: idempotencyHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error paying employee:', error);
      throw error;
    }
  },

  payAllEmployees: async (data) => {
    try {
      // Call the new pay-all endpoint to mark all employees as paid
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.PAYROLL.PAY_ALL), data, {
        headers: idempotencyHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error paying all employees:', error);
      throw error;
    }
  },

  partialPay: async (employeeId, data) => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.PAYROLL.PARTIAL_PAY, employeeId), data, {
        headers: idempotencyHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error processing partial payment:', error);
      throw error;
    }
  },

  removePartialPayment: async (employeeId, paymentId, month) => {
    try {
      const queryString = month ? `?month=${month}` : '';
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.REMOVE_PARTIAL_PAYMENT, employeeId, paymentId)}${queryString}`;
      const response = await httpClient.delete(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error removing partial payment:', error);
      throw error;
    }
  },

  addAdjustment: async (employeeId, data) => {
    try {
      const response = await httpClient.patch(getEndpointPath(ENDPOINTS.PAYROLL.ADJUSTMENTS, employeeId), data);
      return response.data;
    } catch (error) {
      console.error('Error adding adjustment:', error);
      throw error;
    }
  },

  removeAdjustment: async (employeeId, adjustmentId, type, month) => {
    try {
      const queryString = new URLSearchParams({ type, ...(month && { month }) }).toString();
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.REMOVE_ADJUSTMENT, employeeId, adjustmentId)}?${queryString}`;
      const response = await httpClient.delete(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error removing adjustment:', error);
      throw error;
    }
  },

  getEmployeePayroll: async (employeeId, month) => {
    try {
      const queryString = month ? `?month=${month}` : '';
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.BY_EMPLOYEE, employeeId)}${queryString}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching employee payroll:', error);
      throw error;
    }
  },

  getStats: async (month) => {
    try {
      const queryString = month ? `?month=${month}` : '';
      const endpoint = `${getEndpointPath(ENDPOINTS.PAYROLL.STATS_SUMMARY)}${queryString}`;
      const response = await httpClient.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching payroll stats:', error);
      throw error;
    }
  },

  /**
   * Calculate current month payroll
   * @returns {Promise} - Response data with calculation results
   */
  calculateCurrentMonth: async () => {
    try {
      const response = await httpClient.post(getEndpointPath(ENDPOINTS.PAYROLL.CALCULATE_CURRENT_MONTH));
      return response.data;
    } catch (error) {
      console.error('Error calculating current month payroll:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get current month payroll list
   * @returns {Promise} - Response data with payroll list and summary
   */
  getCurrentMonth: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.PAYROLL.CURRENT_MONTH));
      return response.data;
    } catch (error) {
      console.error('Error fetching current month payroll:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Download employee payslip PDF
   * @param {string} employeeId - Employee ID
   * @param {string} month - Month in YYYY-MM format
   * @returns {Promise} - Blob response for PDF download
   */
  downloadPayslip: async (employeeId, month) => {
    if (!isValidId(employeeId, 'Employee')) {
      return createInvalidIdResponse('Employee');
    }
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.PAYROLL.PAYSLIP, employeeId, month), {
        responseType: 'blob', // Important for PDF download
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading payslip:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Download logged-in employee's payslip PDF (any past month with payroll).
   * @param {string} [month] - Month in YYYY-MM format (defaults to current on backend)
   * @returns {Promise} - Blob response for PDF download
   */

  /**
   * Logged-in employee payroll for a month
   * @param {string} [month] - YYYY-MM
   */
  getMyPayrollForMonth: async (month) => {
    try {
      const params = month ? { month } : {};
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.PAYROLL.MY_PAYROLL), { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching my payroll for month:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Months with an actual payroll record for the logged-in employee.
   * Single source of truth for Me salary month picker.
   * @returns {Promise<{ success: boolean, data: string[] }>}
   */
  getMyPayrollMonths: async () => {
    try {
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.PAYROLL.MY_MONTHS));
      const body = response.data;
      const raw = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
      const months = raw
        .map((m) => String(m || '').trim())
        .filter((m) => /^\d{4}-\d{2}$/.test(m));
      return {
        success: body?.success !== false,
        data: months,
      };
    } catch (error) {
      console.error('Error fetching my payroll months:', error);
      throw error.response?.data || error;
    }
  },

  downloadMyPayslip: async (month) => {
    try {
      const params = {};
      if (month) params.month = month;
      const lang = (localStorage.getItem('language') || 'ar').toLowerCase();
      params.lang = lang === 'en' ? 'en' : 'ar';
      const response = await httpClient.get(getEndpointPath(ENDPOINTS.PAYROLL.MY_PAYSLIP), {
        params,
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading my payslip:', error);
      throw error.response?.data || error;
    }
  },
};

export default payrollService;

